import React, { Fragment } from 'react'
import styled, { css } from 'styled-components'

import WithPodiumStyles from './WithPodiumStyles'
import WithForcedMonospace from './WithForcedMonospace'
import WithAnimatedDeltas from './WithAnimatedDeltas'
import { SafeFlagImg } from './SafeImgs'
import { NO_INFORMATION_SYMBOL } from '../unicodeChars'

import { useStore } from '../zustandStore'

const PlayerPanelShared = ({ player }) => {
  if (player == null) return null

  const {
    event,
    shouldUseFullChapterNames
  } = useStore(state => ({
    event: state.event,
    shouldUseFullChapterNames: state.layoutSettings.obsPlayerPanelChapterNames === 'full'
    // this layout setting controls whether the default chapter labels in both player panels
    // should be its monospaced and capitalized short_name or its normally formatted full_name
  }))

  return (
    <Wrapper className='panel-wrapper'>
      <PlayerTitle className='player-title'>
        {player.completedAllChapters && (
          <Rank className='rank'>
            <WithForcedMonospace>{player.rank}</WithForcedMonospace>
          </Rank>
        )}
        <Name className='name'>
          {player.name}
        </Name>
        {player.isCountryPresent && (
          <Flag className='flag'>
            <SafeFlagImg {...player} />
          </Flag>
        )}
      </PlayerTitle>

      <ChapterTimesBody className='chapter-times-body' shouldUseFullChapterNames={shouldUseFullChapterNames} chapterCount={event.chapterNames.length}>
        <GridCell row={1} col={1} className='column-chapter row-total'>Total</GridCell>
        <GridCell row={1} col={2} className='column-time row-total'>
          <GridCellSpacer>
            <div>
              <WithForcedMonospace em={0.515}>
                {player.eventBestTimes.total || NO_INFORMATION_SYMBOL}
              </WithForcedMonospace>
              {player.completedAnyChapters && !player.completedAllChapters && '*'}
            </div>
          </GridCellSpacer>
          {!player.eventBestTimes.total && <TimeColumnSpacer eventBestTimes={player.eventBestTimes} />}
        </GridCell>
        <TotalRowSpacer />

        {event.chapterNames.map((chapterName, rowIndex) => {
          const chapterNameOverride = event.chapters[chapterName].nameOverrides.panelRow

          return (
            <Fragment key={chapterName}>

              {shouldUseFullChapterNames ? (
                <GridCell key={chapterName} row={rowIndex + 2} col={1} className={'column-chapter'}>
                  {chapterNameOverride || event.chapters[chapterName].fullName}
                </GridCell>
              ) : (
                <GridCell key={chapterName} row={rowIndex + 2} col={1} className={'column-chapter' + (chapterNameOverride ? '' : ' capitalized')}>
                  {chapterNameOverride
                    ? <>{chapterNameOverride}</>
                    : <WithForcedMonospace em={0.515}>{chapterName}</WithForcedMonospace>
                  }
                </GridCell>
              )}

              <GridCell row={rowIndex + 2} col={2} className='column-time'>
                <GridCellSpacer isBlank={!player.eventBestTimes[chapterName]}>
                  <WithAnimatedDeltas
                    delta={player.eventChapterDeltas[chapterName]}
                    updatedAt={player.eventBestTimesUpdatedAt[chapterName]}
                    bestTime={player.eventBestTimes[chapterName]}
                    monospaceWidthEm={0.515}
                  >
                    <WithPodiumStyles rank={player.eventChapterRanks[chapterName]} em={0.515}>
                      {player.eventBestTimes[chapterName] || NO_INFORMATION_SYMBOL}
                    </WithPodiumStyles>
                  </WithAnimatedDeltas>
                </GridCellSpacer>
                <TimeColumnSpacer eventBestTimes={player.eventBestTimes} />
              </GridCell>
            </Fragment>
          )
        })}
      </ChapterTimesBody>
    </Wrapper>
  )
}

export default PlayerPanelShared

const Wrapper = styled.div`
  font-size: var(--font-size);
  display: grid;
  grid-template-rows: min-content 1fr;
`

const PlayerTitle = styled.div`
  grid-row: 1;
  grid-column: 1;
  display: grid;
  width: 100%;
  background-color: var(--panel-title-obs-color);
  max-width: 100%;
  ${'' /* overflow: hidden; */}
  grid-template-columns: min-content auto 1fr;
  padding-top: 0.25em;
  padding-bottom: 0.1em;
`

const NameAndRank = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-wrap: nowrap;
  flex-grow: 1;
`

const Name = styled.div`
  grid-row: 1;
  grid-column: 2;
  ${'' /* line-height: 1.4em; */}
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  line-height: 0.9em;
  margin-left: 0.2em;
  padding-bottom: 0.1em;
`

const Rank = styled.div`
  grid-row: 1;
  grid-column: 1;
  color: var(--rank-color);
  display: flex;
  font-weight: bold;
  align-items: center;
  justify-content: center;
  font-size: 0.6em;
  margin-left: 0.24em;
  ${'' /* margin-bottom: 0.1em; */}
`

const Flag = styled.div`
  grid-row: 1;
  grid-column: 3;
  margin: auto calc(var(--flag-width) * 0.2) auto auto;

  > img {
    width: var(--flag-width);
    height: calc(var(--flag-width) * (2/3));
  }
  line-height: 1em;
`

const ChapterTimesBody = styled.div`

  display: grid;
  grid-row: 2;
  grid-column: 1;
  column-gap: 0.5em;
  line-height: 1em;
  grid-template-rows: 4fr repeat(${props => props.chapterCount}, 3fr);

  .row-total {
    color: var(--total-color);
    &.column-time {
      div {justify-content: center;}
    }
  }

  .column-chapter {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    &.capitalized { text-transform: uppercase; }
    /* in "long name" mode, we decrease the font size of chapter names a bit (but not the word 'total') */
    ${(props) => props.shouldUseFullChapterNames && css`
      &:not(.row-total) { font-size: 0.83em; }
    `}
  }

  .column-time {
    margin-right: auto;
  }
`

const GridCell = styled.div`
  grid-row: ${props => props.row};
  grid-column: ${props => props.col};
  display: grid;
  text-align: right;
  &.spacer { visibility: hidden; }
`
const GridCellSpacer = styled.div`
  grid-row: 1;
  grid-column: 1;
  width: 100%;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  ${props => props.isBlank && css`
    justify-content: center;
  `}
  .delta {
    width: 100%;
    display: flex;
    justify-content: flex-end;
  }

  ${'' /* &.spacer {color: #bb222288;} */}
  &.spacer {visibility: hidden;}
`

// these are invisible and either overlaid on the total (visible or not) or the dash where a chapter time would be found
// for the total, it helps center the overall grid by setting its longest row. for a chapter time, it allows the hyphen to be
// "centered" where the the times would appear
const TotalRowSpacer = () => (<>
  <GridCell row={1} col={1} className='column-chapter row-total spacer'>Total</GridCell>
  <GridCell row={1} col={2} className='column-time row-total'>
    <GridCellSpacer className='spacer'>
      <span>
        <WithForcedMonospace em={0.515}>
          00:00.000
        </WithForcedMonospace>
      </span>*
    </GridCellSpacer>
  </GridCell>
</>)

const TimeColumnSpacer = ({eventBestTimes}) => {
  let safeTimesCopy = {...eventBestTimes}
  delete safeTimesCopy.total

  let minuteDigitCount = Math.max(...Object.values(safeTimesCopy).map(t => (t && t.split(':')[0].length) || 0))
  if (minuteDigitCount < 1) {
    minuteDigitCount = 1
  }
  let spacerString = ''
  while (spacerString.length < minuteDigitCount) {
    spacerString += '0'
  }
  spacerString += ':00.000'
  return (
    <GridCellSpacer className='spacer'>
      <span>
        <WithForcedMonospace em={0.515}>
          {spacerString}
        </WithForcedMonospace>
      </span>
    </GridCellSpacer>
  )
}
